// Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React from 'react'
import { withRouter } from 'react-router-dom'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'
import DatePicker from 'react-datepicker'
import qs from 'query-string'

import {
  Spinner,
  Warning,
  SvgIcon,
  Table,
  Button
} from '../'

import {
  decodeBase64
} from '../../utils'

import {
  METRIC_EMPLOYER_DAILY_SUMMARY
} from '../../constants'

const QUERY_METRICS_ON_DAY = gql`
  query($metricSourceId: UUID!, $startOfDay: DateTime!, $endOfDay: DateTime!) {
    metrics(metricsource_Id: $metricSourceId, createdAt_Gte: $startOfDay, createdAt_Lte: $endOfDay) {
      edges {
        node {
          id
          description
          result
          createdAt
          identity {
            id
            employer {
              friendlyName
            }
          }
        }
      }
    }
  }
`

const getInitialState = (location) => {
  const today = moment()

  let defaultState = {
    metricsDate: today,
    lastDateChoice: today,
    yesterday:  moment(today).subtract(1, 'days'),
    prevSortOnColumn: null,
    sortOnColumn: 0,
    columnSortAscending: true
  }

  let queryParams = qs.parse(location.search)
  let numberOfQueryParams = Object.keys(queryParams).length
  if (numberOfQueryParams > 1) {  // expecting tab url param to always be present
    // if initially url was entered, or copy-and-pasted, with filters specified in url search params, set state accordingly
    const filters = getFiltersFromQueryString(queryParams)
    return { ...defaultState, ...filters }
  } else {
    return defaultState
  }
}

const getFiltersFromQueryString = (queryParams) => {
  let filters = { metricsDate: moment(queryParams['date'], 'YYYYMMDDTHHmm') }

  return filters
}


class EmployerKPIMetrics extends React.Component {
  state = getInitialState(this.props.location)

  setQueryString = () => {
    const metricsDate = this.state.metricsDate

    const filters = {
      date: metricsDate.format('YYYYMMDDTHHmm')
    }

    if (Object.keys(filters).length > 0) {
      this.props.history.push({ search: qs.stringify(filters) })
    }
  }

  handleMetricsDateChange = (date, refetch) => (
    this.setState(
      {
        lastDateChoice: moment(this.state.metricsDate),
        metricsDate: date
      },
      () => {
        this.setQueryString()
        refetch({
          startOfDay: moment(date).utc().startOf('day'),
          endOfDay: moment(date).utc().endOf('day')
        })
      }
    )
  )

  setMetricsDateToYesterday = () => (
    this.setState(
      {
        metricsDate: moment(this.state.yesterday),
        lastDateChoice: moment(this.state.metricsDate)
      },
      this.setQueryString
    )
  )

  render() {
    const { metricsDate, today, yesterday, lastDateChoice } = this.state

    return (
      <Query
        query={QUERY_METRICS_ON_DAY}
        variables={{
          metricSourceId: decodeBase64(METRIC_EMPLOYER_DAILY_SUMMARY),
          startOfDay: moment(metricsDate).utc().startOf('day'),
          endOfDay: moment(metricsDate).utc().endOf('day')
        }}>
        {({ loading, error, data, refetch }) => {
          if (loading) {
            return <Spinner type='tail' />
          }

          if (error) {
            return <Warning iconSize='x-small'>Error loading KPI metrics.</Warning>
          }

          if (data.metrics.edges.length === 0) {
            return (
              <div className='stage-main slds-p-top_xx-large slds-p-left_medium' style={{ height: '100vh' }}>
                <div className='slds-grid u-font-size-larger' style={{ alignItems: 'center' }}>
                  <div className='slds-m-right_small'>
                    <SvgIcon
                      variant='utility'
                      symbol='info'
                      size='small'
                      colour='light'
                      className='slds-m-right_small'
                    />
                    {`No metrics found for ${lastDateChoice.format('DD/MM/YYYY')}.`}
                  </div>
                  Instead view KPI metrics for
                  <Button type='reset' onClick={this.setMetricsDateToYesterday} className='slds-p-left_x-small slds-p-right_x-small'>
                    yesterday
                  </Button>
                  or
                  <DatePicker
                    id='metricsDate'
                    selected={metricsDate.isSame(lastDateChoice, 'days')
                      ? null
                      : metricsDate
                    }
                    placeholderText="Select alternate date"
                    onChange={date => this.handleMetricsDateChange(date, refetch)}
                    dateFormat="DD/MM/YYYY"
                    maxDate={yesterday}
                    isClearable={true}
                    showMonthDropdown={true}
                    showYearDropdown={true}
                    autoComplete='off'
                    className='slds-input slds-m-left_x-small'
                  />
                </div>
              </div>
            )
          }

          let metrics = data.metrics.edges

          let employerKpiRows = metrics.map(({ node: metric }) => {
            const kpi = JSON.parse(metric.result)["KPIs"]

            if (kpi) {
              // create flat object to more easily perform sorting operations on
              return {
                "Employer": metric.identity.employer.friendlyName,
                "Reporting 'Clients'": kpi["Active Clients"] + kpi["EarnBacks Only"] + kpi["Active Non-members"],
                "Active Employees": kpi["Active Employees"],
                "Clients": kpi["Clients"],
                "Active Clients": kpi["Active Clients"],
                "Active Non-members": kpi["Active Non-members"],
                "EarnBacks Members": kpi["EarnBacks Members"],
                "EarnBacks Members - Outstanding": kpi["EarnBacks Members - Outstanding"],
                "EarnBacks Only": kpi["EarnBacks Only"],
                "Active - Investments": kpi["Active - Investments"],
                "Active - Insurance": kpi["Active - Insurance"],
                "Active - Credit": kpi["Active - Credit"]
              }
            } else {
              return (
                <tr key={`kpiRow_${metric.id}`}>
                  <td colSpan={12}>
                    <Warning iconSize='x-small'>
                      No KPI metrics found on selected date for
                      <span className='slds-p-left_xx-small'>
                        {metric.identity.employer.friendlyName}
                      </span>.
                    </Warning>
                  </td>
                </tr>
              )
            }
          })

          // perform sort according to chosen column in ascending or descending fashion
          employerKpiRows.sort((a, b) => {
            const column = Object.keys(a)[this.state.sortOnColumn]

            if (this.state.columnSortAscending) {
              return a[column] < b[column]
                ? 1
                : -1
            } else {
              return a[column] > b[column]
                ? 1
                : -1
            }
          })

          // now transform sorted data into table rows to render
          employerKpiRows = employerKpiRows.map(row => {
            const employer = Object.keys(row)[0]

            return (
              <tr key={`kpiRow_${row[employer]}`}>
                {Object.keys(row).map((column, index) => (
                  index === 0
                    ? <td className='slds-truncate slds-is-absolute' title={column}>
                        <strong>{row[column]}</strong>
                      </td>
                    : <td title={column}>
                        {row[column]}
                      </td>
                ))}
              </tr>
            )
          })

          return (
            <div className='slds-scrollable_x'>
              <div className='slds-grid slds-p-around_small' style={{ alignItems: 'center' }}>
                Viewing KPI metrics for
                <DatePicker
                  id='metricsDate'
                  selected={metricsDate}
                  placeholderText="Select alternate date"
                  onChange={date => this.handleMetricsDateChange(date, refetch)}
                  dateFormat="DD/MM/YYYY"
                  maxDate={today}
                  showMonthDropdown={true}
                  showYearDropdown={true}
                  autoComplete='off'
                  className='slds-input slds-m-left_small'
                />
              </div>
              <Table
                id='employerKPI'
                columns={[
                  'Employer', "Reporting 'Clients'", 'Active Employees', 'Clients', 'Active Clients', 'Active Non-members',
                  'EarnBacks Members', 'EarnBacks Members - Outstanding', 'EarnBacks Only',
                  'Active - Investments', 'Active - Insurance', 'Active - Credit'
                ]}
                rows={employerKpiRows}
                objectRows={true}
                noCellPadding={true}
                headerCellWrap={true}
                firstColumnWidth='14%'
                sortedColumnIndex={this.state.sortOnColumn}
                sortedDirection={this.state.columnSortAscending
                  ? 'ascending'
                  : 'descending'
                }
                onColumnClick={columnIndex => (
                  this.setState(prevState => (
                    prevState.sortOnColumn === columnIndex
                    ? {
                        columnSortAscending: !prevState.columnSortAscending,
                        sortOnColumn: columnIndex
                      }
                    : {
                        columnSortAscending: true,
                        sortOnColumn: columnIndex,
                        prevSortOnColumn: prevState.sortOnColumn
                      }
                  ))
                )}
                className='slds-scrollable_x'
              />
            </div>
          )
        }}
      </Query>
    )
  }
}

export default withRouter(EmployerKPIMetrics)



// WEBPACK FOOTER //
// ./src/components/employer-kpi-metrics/index.js